//  ----------      START   IMPORT  BLOCK       ----------
import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { LanguageEnum } from '../../Types/Enums/LanguageEnum';
//  ----------      END   IMPORT  BLOCK       ----------

/**
 * Shape of any vocabulary dictionary following the [es, en] array pattern.
 */
export type VocabDictionary = Record<string, string[] | readonly string[]>;

// Define the context shape using the explicit enum type
interface LanguageContextType {
  lan: LanguageEnum;
  setLan: (langIndex: LanguageEnum) => void;
  /**
   * Centralized helper to access localized strings cleanly:
   * usage: t(VocabProMenu, 'title')
   */
  t: <D extends VocabDictionary>(dict: D, key: keyof D) => string;
}

// Initialize with a default value matching the interface shape
const LanguageContext = createContext<LanguageContextType | null>(null);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface ProviderProps {
  children: ReactNode;
}

/**
 * Provides language index globally.
 * Defaults to LanguageEnum.SPANISH (0).
 */
export const LanguageProvider = ({ children }: ProviderProps) => {
  // 🎯 Initialize from localStorage if it exists, otherwise default to Spanish
  const [lan, setLanState] = useState<LanguageEnum>(() => {
    const savedLanguage = localStorage.getItem('app_language');
    if (savedLanguage !== null) {
      const parsed = parseInt(savedLanguage, 10);
      if (parsed in LanguageEnum) {
        return parsed as LanguageEnum;
      }
    }
    return LanguageEnum.SPANISH;
  });

  const setLan = useCallback((langIndex: LanguageEnum) => {
    setLanState(langIndex);
    localStorage.setItem('app_language', langIndex.toString());
  }, []);

  /**
   * Strongly-typed dictionary lookup helper.
   * Directly indexes dict[key][lan], falling back to English or key string if array index is missing.
   */
  const t = useCallback(
    <D extends VocabDictionary>(dict: D, key: keyof D): string => {
      const entry = dict[key];
      if (!entry) return String(key);
      return entry[lan] ?? entry[LanguageEnum.ENGLISH] ?? entry[0] ?? String(key);
    },
    [lan]
  );

  const value = useMemo(
    () => ({
      lan,
      setLan,
      t,
    }),
    [lan, setLan, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};